import { authHeaders } from 'functions/authHeader';
import configs from 'configs';
import request from 'functions/api/request';
import moment from 'moment';

const objectIterator = (obj, cb) => {
  for (const key in obj) {
    // eslint-disable-next-line no-param-reassign
    obj[key] = cb(obj[key]);
    if (obj[key] !== null && typeof obj[key] === 'object') {
      objectIterator(obj[key], cb);
    }
  }
};

const apiUrl = configs.beApiUrl;

const signup = (data, type) => {
  const requestOptions = {
    url: type ? `${apiUrl}/user/signup/${type}` : `${apiUrl}/user/signup`,
    data,
    json: true,
  };

  return request.post(requestOptions);
};

const login = async (data, type) => {
  let response = null;

  const requestOptions = {
    url: type ? `${apiUrl}/user/login/${type}` : `${apiUrl}/user/login`,
    data,
    json: true,
  };

  try {
    response = await request.post(requestOptions);
  } catch (ex) {
    console.error('Error occurred, while updating existing user profile');
    const errorCode = ex?.response?.status || undefined;
    return { error: ex.message, errorCode, ...ex } || null;
  }

  return response;
};

const passwordlessLogin = async (data) => {
  let response = null;

  const requestOptions = {
    url: `${apiUrl}/user/passwordless-login`,
    data,
    json: true,
  };

  try {
    response = await request.post(requestOptions);
  } catch (ex) {
    console.error('Error occurred, while updating existing user profile');
    const errorCode = ex?.response?.status || undefined;
    return { error: ex.message, errorCode } || null;
  }

  return response;
};

const verify = async (data) => {
  let response = null;

  const requestOptions = {
    url: `${apiUrl}/user/verify`,
    data,
    json: true,
  };

  try {
    response = await request.post(requestOptions);
  } catch (ex) {
    console.error(ex);
    throw ex;
  }

  return response;
};

const forgotPassword = async (email) => {
  let response = null;

  const requestOptions = {
    url: `${apiUrl}/user/forgot-password`,
    data: email,
    json: true,
  };

  try {
    response = await request.post(requestOptions);
  } catch (ex) {
    console.error(ex);
    throw ex;
  }

  return response;
};

const resetPassword = async (data) => {
  let response = null;

  const requestOptions = {
    url: `${apiUrl}/user/reset-password`,
    data,
    json: true,
  };

  try {
    response = await request.post(requestOptions);
  } catch (ex) {
    console.error(ex);
    return ex;
  }

  return response;
};

const createTempUser = async (user) => {
  let response = null;

  const requestOptions = {
    url: `${apiUrl}/user/temp`,
    data: user,
    json: true,
  };

  try {
    response = await request.post(requestOptions);
  } catch (ex) {
    console.error('Error occurred, while creating new user profile:', ex);
    throw ex;
  }

  return response;
};

const createFullUser = async (data) => {
  let response = null;

  const requestOptions = {
    url: `${apiUrl}/user`,
    headers: await authHeaders(),
    data,
    json: true,
  };

  try {
    response = await request.post(requestOptions);
  } catch (ex) {
    console.error('Error occurred, while creating new user profile for existing user:', ex);
    throw ex;
  }

  return response;
};

const updateUser = async (data) => {
  let response = null;

  objectIterator(data, (v) => {
    if (moment(v, moment.ISO_8601, true).isValid()) {
      return moment(v).format('YYYY-MM-DD');
    }
    return v;
  });

  const requestOptions = {
    url: `${apiUrl}/user`,
    headers: await authHeaders(),
    data,
    json: true,
  };

  try {
    response = await request.put(requestOptions);
  } catch (ex) {
    console.error('Error occurred, while updating existing user profile', ex);
    throw ex;
  }

  return response;
};

const getUser = async (data) => {
  let response = null;

  const requestOptions = {
    url: `${apiUrl}/user/profile`,
    qs: {
      lang: data?.lang || null,
    },
    headers: await authHeaders(),
    json: true,
  };

  try {
    response = await request.get(requestOptions);
  } catch (ex) {
    console.error('Error occurred, while getting user profile', ex);
    throw ex;
  }

  return response;
};

const updateUserCustomerRef = async ({ customerRef }) => {
  // corner case. It is going to be used once in a while
  const user = await getUser();
  const updatedUser = await updateUser({
    ...user,
    personal: {
      ...user.personal,
      customerRef,
    },
  });
  return updatedUser;
};

const getCrmChatAuthToken = async () => {
  let response = null;

  const requestOptions = {
    url: `${apiUrl}/user/crm/chat/token`,
    headers: await authHeaders(),
    json: true,
  };

  try {
    response = await request.get(requestOptions);
  } catch (ex) {
    console.error('Error occurred, while getting chat token:', ex);
    throw ex;
  }

  return response && response.data;
};

const registerUserRequests = async (data) => {
  let response = null;

  const requestOptions = {
    url: `${apiUrl}/user/crm/requests/register`,
    headers: await authHeaders(),
    json: true,
    data,
  };

  try {
    response = await request.post(requestOptions);
  } catch (ex) {
    console.error('Error occurred, register crm user request:', ex);
    throw ex;
  }

  if (!response?.success) {
    throw new Error('Wrong response from tj-be');
  }

  return response.success;
};

const updateIntercomUser = async (data) => {
  let response = null;

  const requestOptions = {
    url: `${apiUrl}/user/intercom/update`,
    headers: await authHeaders(),
    json: true,
    data,
  };

  try {
    response = await request.post(requestOptions);
  } catch (ex) {
    console.error('Error occurred, update intercom user request:', ex);
    throw ex;
  }

  if (!response?.success) {
    throw new Error('Wrong response from tj-be');
  }

  return response.success;
};

export const authenticationApi = {
  signup,
  login,
  passwordlessLogin,
  verify,
  forgotPassword,
  resetPassword,
  createFullUser,
  createTempUser,
  getUser,
  updateUser,
  getCrmChatAuthToken,
  registerUserRequests,
  updateIntercomUser,
  updateUserCustomerRef,
};
